import * as React from 'react'

export type InitialValueFn<T> = (storedValue?: T) => T
// https://usehooks.com/useLocalStorage/
export function useSessionStorage<T = any>(
  key: string,
  initialValue: T | InitialValueFn<T>
) {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = React.useState(() => {
    try {
      // Get from local storage by key
      const item = window.sessionStorage.getItem(key)
      const itemValue = item ? JSON.parse(item) : undefined
      // Parse stored json or if none return initialValue
      return typeof initialValue === 'function'
        ? (initialValue as any)(itemValue)
        : itemValue || initialValue
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log(error)
      return typeof initialValue === 'function'
        ? (initialValue as any)()
        : initialValue
    }
  })

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = React.useCallback(
    (value: T) => {
      try {
        // Allow value to be a function so we have same API as useState
        const valueToStore =
          value instanceof Function ? value(storedValue) : value
        // Save state
        setStoredValue(valueToStore)
        // Save to local storage
        window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
      } catch (error) {
        // A more advanced implementation would handle the error case
        // tslint:disable-next-line:no-console
        console.log(error)
      }
    },
    [key, storedValue, setStoredValue]
  )

  return [storedValue, setValue] as [T, (newValue: T) => void]
}
